import _ from 'lodash';
import Promise from 'bluebird';
import getHelpers from '../helpers';
const api = getHelpers('api');

// initial state
const state = {
    ...window.store,
    notifications: [],
    product: null,
    newCartItem: null,
    timestamp: Date.now(),
}

// getters
const getters = {
    customerLogin: (state) => state.customerLogin,
    customer: (state) => state.customer,
    carts: (state) => state.carts,
    cart: (state) => state.cart,
    order: (state) => state.order,
    product: (state) => state.product,
    newCartItem: (state) => state.newCartItem,
    notifications: (state) => state.notifications,
    timestamp: (state) => state.timestamp,
    route: (state) => (name, params) => {
        let path = '/' + _.get(state, ['routes', name]);

        _.forEach(params, (value, key) => {
            path = _.replace(path, new RegExp(`{${key}[?]?}`), value);
        })

        return path;
    }
}

const actions = {
    setProduct({ commit }, res) {
        commit('setProduct', res);
        return res;
    },

    getProduct({ commit }, pid) {
        return api.getData(`/product/${pid}`, {
            include: 'prices,attributes,images'
        }).then(res => {
            commit('setProduct', res);
            return res;
        });
    },

    updateProfile({ commit }, data) {
        return api
            .patchData(`/my/profile`, {}, data)
            .then(res => {
                commit('addNotification', {
                    type: 'message',
                    message: 'Profile updated successfully',
                });
                commit('updateProfile', res);
                return res;
            })
    },

    updateCustomer({ commit, state }, data) {
        const customerId = _.get(state, 'customer.id');
        return api
            .patchData(`/my/customer/${customerId}`, {}, { origin: data })
            .then(res => {
                commit('addNotification', {
                    type: 'message',
                    message: 'Profile updated successfully',
                });
                commit('updateCustomer', res);
            })
    },

    createOrder({ commit }, data) {
        return api.postData(`/my/orders`, {}, data)
            .then(res => {
                // commit('addNotification', {
                //     type: 'message',
                //     message: 'Your order is ready',
                // });
                commit('setOrder', res);
                return res;
            })
    },

    setCart({ commit }, cart) {
        return api
            .getData(`/my/cart`, {
                cart_id: cart.id,
                include: 'items',
            })
            .then(res => {
                commit('setCart', res);
                return res;
            })
    },

    setCartItems({ commit }, cartItems) {
        commit('setCartItems', cartItems);
    },

    updateCart({ commit }, data) {
        return api
            .patchData(`/my/cart`, {}, data)
            .then(res => {
                commit('addNotification', {
                    type: 'message',
                    message: 'Cart updated successfully',
                });
                commit('updateCart', res);
                return res;
            })
    },

    updateCartById({ commit }, input) {
        const id = _.get(input, 'id');
        const data = _.get(input, 'data', {});
        
        return api
            .patchData(`/my/carts/${id}`, {}, data)
            .then(res => {
                commit('addNotification', {
                    type: 'message',
                    message: 'Cart updated successfully',
                });
                // commit('updateCart', res);
                return res;
            })
    },

    createCart({ commit }, data) {
        return api
            .postData(`/my/carts`, {}, data)
            .then(res => {
                commit('addNotification', {
                    type: 'message',
                    message: 'New cart has been created',
                });
                commit('createCart', res);
                return res;
            })
    },

    deleteCart({ commit }, id) {
        return api
            .delData(`/my/cart/${id}`)
            .then(res => {
                commit('addNotification', {
                    type: 'message',
                    message: 'Cart has been deleted',
                });
                commit('deleteCart', res);
                return res;
            })
    },

    getCartItems({ commit }, payload) {
        const { cart_id } = payload;
        return api.getData(`/my/cart/items`, { cart_id })
            .then(res => {
                commit('setCartItems', res);
                return res;
            })
    },

    createCartItem({ commit }, payload) {
        const { cart_id, item } = payload;

        return api.postData(`/my/cart/items`, {}, _.merge({ cart_id }, _.get(item, 'attributes', {})))
            .then(() => api.getData(`/my/cart/items`, { cart_id }))
            .then(res => {
                if (_.get(payload, 'notification', true)) {
                    commit('addNotification', {
                        type: 'message',
                        message: 'Item has been added to cart',
                    });
                }

                commit('setCartAttribute', _.get(res, 'included.parent'));
                commit('setCartItems', res);

                const newCartItem = _.find(res.data,
                    (cartItem) => _.get(cartItem, 'attributes.price_id') == _.get(item, 'attributes.price_id')
                );
                commit('setNewCartItem', newCartItem);
                return res;
            });
    },

    updateCartItem({ commit }, payload) {
        const { cart_id, item } = payload;

        return api.patchData(`/my/cart/item/${item.id}`, {}, _.merge({ cart_id }, _.get(item, 'attributes', {})))
            .then(res => {
                if (_.get(payload, 'notification', true)) {
                    commit('addNotification', {
                        type: 'message',
                        message: 'Item has been updated',
                    });
                }

                commit('setCartAttribute', _.get(res, 'included.parent'));
                commit('updateCartItems', [
                    { id: res.id, $updated: res }
                ]);
                return res;
            })
    },

    deleteCartItem({ commit }, payload) {
        const { cart_id, item } = payload;
        return api.delData(`/my/cart/item/${item.id}`, { cart_id })
            .then(res => {
                if (_.get(payload, 'notification', true)) {
                    commit('addNotification', {
                        type: 'message',
                        message: 'Item has been deleted',
                    });
                }

                commit('setCartAttribute', _.get(res, 'included.parent'));
                commit('updateCartItems', [
                    { id: res.id, $deleted: true }
                ]);
                return res;
            })
    },

    addNotification({ commit }, payload) {
        commit('addNotification', payload);
    },

    createDeliveryAddress({ commit }, data) {
        return api.postData('/my/account/delivery_addresses', {}, data)
            .then(res => {
                commit('addNotification', {
                    type: 'message',
                    message: 'Delivery address created successfully',
                });
                commit('addDeliveryAddress', res);
                return res;
            })
    },

    updateDeliveryAddresses({ commit }, payload) {
        const { data } = payload;

        return api.putData('/my/account/delivery_addresses', {}, { data })
            .then(res => {
                commit('addNotification', {
                    type: 'message',
                    message: 'Delivery address updated successfully',
                });
                commit('updateDeliveryAddresses', res);
                return res;
            })
    },
}

// mutations
const mutations = {
    setCart(state, cart) {
        _.set(state, 'cart', cart);
    },

    setCartAttribute(state, cart) {
        const attributes = _.chain(state).get('cart.attributes').merge(_.get(cart, 'attributes', {})).value();
        _.set(state, 'cart.attributes', attributes);
        _.set(state, 'timestamp', Date.now());
    },

    setOrder(state, order) {
        _.set(state, 'order', order);
        _.set(state, 'timestamp', Date.now());
    },

    setProduct(state, response) {
        _.set(state, 'product', response);
        _.set(state, 'timestamp', Date.now());
    },

    updateProfile(state, response) {
        _.set(state, 'customerLogin', response);
        _.set(state, 'timestamp', Date.now());
    },

    updateCustomer(state, response) {
        _.set(state, 'customer', response);
        _.set(state, 'timestamp', Date.now());
    },

    updateCart(state, response) {
        _.set(state, 'cart', response);
        _.set(state, 'timestamp', Date.now());
    },

    createCart(state, response) {
        const carts = _.get(state, 'carts.data', []);
        _.set(state, 'carts.data', _.concat(carts, [response]));
        _.set(state, 'timestamp', Date.now());
    },

    deleteCart(state, response) {
        const carts = _.get(state, 'carts.data', []);
        _.set(state, 'carts.data', _.reject(carts, (c) => c.id == response.id));
        _.set(state, 'timestamp', Date.now());
    },

	setCartItems(state, response) {
        _.set(state, 'cart.included.items', response);
        _.set(state, 'timestamp', Date.now());
	},

    setNewCartItem(state, response) {
        _.set(state, 'newCartItem', response);
    },

    updateCartItems(state, data) {
        let items = _.get(state, 'cart.included.items.data', []);

        _.forEach(data, (item) => {
            /*||*/ if (_.has(item, '$created')) {
                items = _.concat(items, item.$created);

            } else if (_.has(item, '$updated')) {
                const idx = _.findIndex(items, (it) => it.id == item.id);
                items[idx] = item.$updated;

            } else if (_.has(item, '$deleted')) {
                items = _.reject(items, (it) => it.id == item.id);
            }
        })

        _.set(state, 'cart.included.items.data', items);
        _.set(state, 'timestamp', Date.now());
    },

    addDeliveryAddress(state, response) {
        let data = _.get(state, 'customer.included.delivery_addresses.data', []);
        data = _.concat(data, response);

        _.set(state, 'customer.included.delivery_addresses.data', data);
        _.set(state, 'timestamp', Date.now());
    },

    updateDeliveryAddresses(state, response) {
        const { updated, deleted, attached, detached } = response.meta;

        let data = _.get(state, 'customer.included.delivery_addresses.data', []);

        // replacing updated items
        data = _.map(data, item => {
            const replacingItem = _.find(response.data, (itemData => itemData.id == item.id));
            return replacingItem || item;
        });

        // filter out deleted items
        data = _.filter(data, item => {
            return _.indexOf(deleted, item.id) < 0;
        });

        // add items attached
        data = _.concat(data, _.filter(response.data, item => _.indexOf(attached, item.id) >= 0));

        // filter out detached items
        data = _.filter(data, item => _.indexOf(detached, item.id) < 0);

        _.set(state, 'customer.included.delivery_addresses.data', data);
        _.set(state, 'timestamp', Date.now());
    },

    addNotification(state, payload) {
        _.set(state, 'notifications', _.concat(state.notifications, [payload]));
    },
}

export default {
    state,
    getters,
    actions,
    mutations,
}
